import ThemedQrcode from '@/components/ThemedQrcode'
import { PageRoutes } from '@/constants/PageRoutes'
import { APP_CONFIG } from '@/project-config'
import classNames from 'classnames'
import { PageRoutes as CloudComputingPowerRoutes } from 'cloud-computing-power/constants/PageRoutes'

import { useCommon } from '@/hooks/use-common'
import { FooterOtherOverride } from '@/project-config/override/components/FooterOtherOverride'
import { legalTextEntries, serviceAgreementTextEntries } from '@/project-config/override/lang-entries'
import { ElCollapse, ElCollapseItem } from 'element-plus'
import { useI18n } from 'i18n-utils'
import { PlatformName } from 'platform-config'
import { useEnvStore, useLanguageStore, useProjectTheme, useResponsive } from 'store'
import { toCamelCase } from 'utils'
import './FooterView.scss'
import { HeaderAppDownload } from './HeaderAppDownload'
import { MainLayout } from './MainLayout'

export const FooterView = defineComponent({
  setup() {
    const themeStore = useProjectTheme()
    const theme = computed(() => themeStore.theme)
    const { t } = useI18n()
    const envStore = useEnvStore()
    const languageStore = useLanguageStore()
    const route = useRoute()
    const enforcementRequestEmail = import.meta.env.VITE_ENFORCEMENT_REQUEST_EMAIL
    const HIDE_FOOTER_ROUTES = [PageRoutes.SpotTrade, PageRoutes.ContractTrade]
    const responsive = useResponsive()
    // const legalFiles = useLegalFiles()
    const { isTabbarPage } = useCommon()
    /** 联系方式 */
    const contactUs = computed<ApiResponse.ContactUs[]>(() => {
      return envStore.config?.contactUs || []
    })

    const getFooterArticleUrl = (item: ApiResponse.FooterArticleConfig) => {
      let url = item.url
      if (item.language) {
        const key = toCamelCase(`url_${languageStore.currentLanguage}`)
        url = item.language[key]
      }
      return url || item.url
    }

    const getContactUsUrl = (item: ApiResponse.ContactUs) => {
      return getFooterArticleUrl(item)
    }

    /** 关于我们 */
    const aboutUs = computed(() => {
      return envStore.config?.about || []
    })

    /** 服务条款 */
    const clause = computed(() => {
      return envStore.config?.clause || []
    })

    const law = computed(() => {
      return envStore.config?.law || []
    })

    const openLink = (item: ApiResponse.FooterArticleConfig) => {
      const url = getFooterArticleUrl(item)
      window.open(url, '_blank')
    }

    const openClauseLink = (item: ApiResponse.FooterArticleConfig) => {
      const url = getFooterArticleUrl(item)
      window.open(url, '_blank')
    }

    const isHideFooter = computed(() => {
      return HIDE_FOOTER_ROUTES.includes(route.path as PageRoutes)
    })

    const openLegalFileLink = (item: ApiResponse.FooterArticleConfig) => {
      window.open(item.url, '_blank')
    }
    const aboutUsActives = ref(['4'])

    const lawEnforcementLink: ApiResponse.FooterArticleConfig = {
      url: PageRoutes.LegalCompliance,
      title: '执法请求',
    }

    const oriUrl = computed(() => {
      if (envStore.isEmptyConfig) return ''
      const language = envStore.config.qrcode.language
      if (language) {
        const key = toCamelCase(`url_${languageStore.currentLanguage}`)
        if (language[key]) {
          return language[key]
        }
      }
      return envStore.config.qrcode.oriUrl
    })

    /** 是否显示服务条款 */
    const isShowClause = computed(() => {
      if (APP_CONFIG.isCompliant && APP_CONFIG.compliantPosition === 'footer') {
        return true
      }
      return clause.value.length > 0
    })

    return () =>
      isHideFooter.value ? null : (
        <div
          class={classNames('footer-view-layout', {
            'is-tabbar-page': isTabbarPage.value,
            'user-center': route.path.includes(PageRoutes.UserCenter),
            'cloud-computing-power': route.path.includes(CloudComputingPowerRoutes.GoodDetail),
          })}
        >
          <MainLayout class={'footer-view-main-layout'}>
            <div class="footer-view-container">
              <div class="left">
                <div class="logo">
                  <img width={theme.value.footerLogoWidth || 200} src={theme.value.logo} alt={PlatformName} />
                </div>
                <div class="desc">
                  <span>{t('让金融更高效，让财富更自由')}</span>
                </div>
                <div class="contact-us">
                  {contactUs.value.map((item) => (
                    <a href={getContactUsUrl(item)} target="_blank" rel="noreferrer">
                      <img width={18} src={item.src} alt={item.title} />
                    </a>
                  ))}
                </div>
                <div>
                  {enforcementRequestEmail && (
                    <div class="mt-4">
                      <div>
                        <span class="email-label">{t('执法请求联系邮箱：')}</span>
                      </div>
                      <a class="link email-link text-[16px]" href={`mailto:${enforcementRequestEmail}`} target="_blank" rel="noreferrer">
                        {enforcementRequestEmail}
                      </a>
                    </div>
                  )}
                </div>
                <div class="copyright">
                  <span>{t('© {start}-{end} {platformName} 版权所有', { platformName: t(PlatformName), start: 2018, end: new Date().getFullYear() })}</span>
                </div>
              </div>
              {!responsive.isMobile && (
                <div class="pc-us">
                  {aboutUs.value.length > 0 && (
                    <div class="about-us link-list">
                      <div class="title">{t('关于我们')}</div>
                      <div class="content">
                        {aboutUs.value.map((item) => (
                          <div class="item">
                            <div class="link" onClick={() => openLink(item)}>
                              {t(item.title)}
                            </div>
                          </div>
                        ))}
                        {APP_CONFIG.aboutUs?.map((item) => (
                          <div class="item">
                            <div class="link" onClick={() => item.onClick?.()}>
                              {t(item.title)}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {isShowClause.value && (
                    <div class="clause link-list">
                      <div class="title">{t(serviceAgreementTextEntries)}</div>
                      <div class="content">
                        {clause.value.map((item) => (
                          <div class="item">
                            <div class="link" onClick={() => openClauseLink(item)}>
                              {t(item.title)}
                            </div>
                          </div>
                        ))}
                        {APP_CONFIG.isCompliant && APP_CONFIG.compliantPosition === 'footer' && (
                          <div class="item">
                            <div class="link" onClick={() => openLegalFileLink(lawEnforcementLink)}>
                              {t(lawEnforcementLink.title)}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {APP_CONFIG.isCompliant && APP_CONFIG.isShowLegalFile && law.value.length > 0 && (
                    <div class="clause link-list">
                      <div class="title">{t(legalTextEntries)}</div>
                      <div class="content">
                        {law.value.map((item) => (
                          <div class="item">
                            <div class="link" onClick={() => openLegalFileLink(item)}>
                              {t(item.title)}
                            </div>
                          </div>
                        ))}
                        {APP_CONFIG.featureList?.map((item) => {
                          if (item.url === 'customAppDownload') {
                            return (
                              <div class={'item'}>
                                <div class={'link'}>
                                  <HeaderAppDownload showDownloadIcon={false} />
                                </div>
                              </div>
                            )
                          }
                          return (
                            <div class="item">
                              <div class="link" onClick={() => openLegalFileLink(item)}>
                                {t(item.title)}
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )}

                  <FooterOtherOverride />
                  <div class="qr-wrap pc">
                    <div class="title">{t('官方TG群')}</div>
                    <div class="qrcode-img">
                      <ThemedQrcode size={78} value={oriUrl.value} />
                    </div>
                  </div>
                </div>
              )}
              {responsive.isMobile && (
                <div class="mobile-us">
                  <ElCollapse modelValue={aboutUsActives.value}>
                    <ElCollapseItem name={'1'}>
                      {{
                        title: () => <div class={'us-title'}>{t('关于我们')}</div>,
                        default: () => (
                          <div class={'us-content'}>
                            {aboutUs.value.map((item) => (
                              <div class="us-content-item">
                                <div class="link" onClick={() => openLink(item)}>
                                  {t(item.title)}
                                </div>
                              </div>
                            ))}
                            {APP_CONFIG.aboutUs?.map((item) => (
                              <div class="us-content-item">
                                <div class="link" onClick={() => item.onClick?.()}>
                                  {t(item.title)}
                                </div>
                              </div>
                            ))}
                          </div>
                        ),
                      }}
                    </ElCollapseItem>
                    <ElCollapseItem name={'2'}>
                      {{
                        title: () => <div class={'us-title'}>{t(serviceAgreementTextEntries)}</div>,
                        default: () => (
                          <div class={'us-content'}>
                            {clause.value.map((item) => (
                              <div class="us-content-item">
                                <div class="link" onClick={() => openClauseLink(item)}>
                                  {t(item.title)}
                                </div>
                              </div>
                            ))}
                            {APP_CONFIG.isCompliant && APP_CONFIG.compliantPosition === 'footer' && (
                              <div class="us-content-item">
                                <div class="link" onClick={() => openLegalFileLink(lawEnforcementLink)}>
                                  {t(lawEnforcementLink.title)}
                                </div>
                              </div>
                            )}
                          </div>
                        ),
                      }}
                    </ElCollapseItem>

                    {APP_CONFIG.isCompliant && law.value.length > 0 && (
                      <ElCollapseItem name={'5'}>
                        {{
                          title: () => <div class={'us-title'}>{t(legalTextEntries)}</div>,
                          default: () => (
                            <div class={'us-content'}>
                              {law.value.map((item) => (
                                <div class="us-content-item">
                                  <div class="link" onClick={() => openLegalFileLink(item)}>
                                    {t(item.title)}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ),
                        }}
                      </ElCollapseItem>
                    )}

                    <FooterOtherOverride />
                    <ElCollapseItem name={'4'}>
                      {{
                        title: () => <div class={'us-title'}>{t('官方TG群')}</div>,
                        default: () => (
                          <div class={'us-content'}>
                            <ThemedQrcode size={78} value={oriUrl.value} />
                          </div>
                        ),
                      }}
                    </ElCollapseItem>
                  </ElCollapse>
                </div>
              )}
            </div>

            {/* 风险提示 */}
            <div class="risk-warning">
              <h2 class="risk-warning-title">{t('风险提示')}</h2>
              <div class="risk-warning-content">
                <p class="risk-warning-content-item">
                  {t(
                    '加密货币、加密衍生品属于创新的金融产品，其价格具备巨大的波动性和较高的投资风险。尽管{platformName}尽力为您提供简单便捷的交易工具，但交易本身仍是一项专业度高的事情，无论您是交易现货或带有杠杆的衍生产品，或者购买理财产品，都有导致快速亏损甚至是损失所有本金的可能。您应该充分了解{platformName}所提供产品的主要功能和所涉及的主要风险，并始终审慎评估相关风险与您自身的风险承受能力是否匹配。如有必要，请在投资前寻求专业和独立的建议。您明确并同意您已阅读、理解并接受{platformName}所有条款。',
                    {
                      platformName: t(PlatformName),
                    },
                  )}
                </p>
                <p class="risk-warning-content-item">
                  {t(
                    '通过跟单或复制他人交易进行投资风险极高，即使跟单或复制的是顶级交易员的交易也是如此。{platformName}社群成员过去的表现并非代表他未来表现。 {platformName}作为您的加密社交交易平台，内容由其社群成员提供，并非{platformName}提供或代表 {platformName}提供的建议或推荐。',
                    {
                      platformName: t(PlatformName),
                    },
                  )}
                </p>
              </div>
            </div>
          </MainLayout>
        </div>
      )
  },
})
